import { reactive, ref } from 'vue';
import { getFamiliarBonusStats } from './familiar.js';
import { computePlayerFinalStats } from '../domain/stats/playerStats.js';
import { getReceptionistBonusStats } from './receptionist.js';
import { socket, serverOnline } from './socket.js';
import { checkCodexUnlock } from './codex.js';
import { initContentRegistry, REGISTRIES } from './contentRegistry.js';
import { setElementsRegistry } from '../domain/combat/elements.js';
import { setStatusEffectsRegistry } from '../domain/combat/statusEffects.js';
import {
  LYRA_STARTER_ITEMS,
  LYRA_STARTER_OUTFIT,
  RETIRED_LYRA_DERIVED_ADVENTURER_IDS,
  TEMPORARILY_HIDDEN_LYRA_ITEM_IDS,
} from '../shared/lyraStarterOutfit';
import {
  GUILD_RANKS,
  PLAYER_LEVEL_MAX,
  certificationMaxLevel,
  isGuildRank,
  minimumCertificationForLevel,
  rankForReputation,
  xpNeededForLevel,
  type GuildRank,
} from '../domain/progression/guild';
import { type EquipmentInstance } from '../domain/equipment/procedural';
import { migrateLegacyEquipmentState } from '../domain/equipment/migration';
import { GLOBAL_ELEMENT_RATIO } from '../domain/progression/statRules';
export { GLOBAL_ELEMENT_RATIO };
import { ITEMS as BASE_ITEMS } from '../domain/progression/items';
import { migrateConsumableInventoryState } from '../domain/consumables/inventory';
import { accountStorageKey, isAccountPersistenceBlocked, readAccountItem, storedAccountId, writeAccountItem } from './accountStorage';
import { canonicalizePublicAssetUrls, resolvePublicAssetUrls } from '../shared/publicAssets';

let _contentRegistries: any = null;
export function setContentRegistries(registries: any) {
  _contentRegistries = registries;
}

export const DEFAULT_STATE = {
  wallet: { cuivres: 0, essence: 0, totalWithdrawn: 0, history: [], transactions: [] },
  // Carnet des rencontres NFC (Carte d'Aventurier) : { accountId, nom, count, lastAt }
  rencontres: [],
  // 🌌 Dons de sponsors réclamés (rift 4e mur) : { [riftId]: timestamp }
  sponsorGifts: {},
  inventory: { items: [], maxSlots: 20, stackSize: 10 },
  player: {
    name: 'Aventurier',
    classe: null,
    level: 1,
    xp: 0,
    xpTotal: 0,
    reputation: 0, // rang = réputation d'entraide (maj-05 §3.7), pas le combat
    certificationRank: 'G' as GuildRank,
    // Legacy (dérivées d'affichage). Le pool PRIMAIRE est retiré (modèle v2 : pool unique).
    stats: { PV: 100, ARMURE: 10, RM: 0, VIT: 10 },
    statPoints: 0,
    stats_added: { PV: 0, ARMURE: 0, RM: 0, VIT: 0 },
    // Attributs fondamentaux (modèle v2) : UN seul pool `secondaryStatPoints`,
    // allocation LIBRE sur les 9 attributs. ESP = Esprit (mana dédié).
    secondaryStats: { VIT: 10, ESP: 10, END: 10, FOR: 10, INT: 10, SAG: 10, CHA: 10, DEX: 10, AGI: 10 },
    secondaryStatPoints: 0,
    secondaryStatsAdded: { VIT: 0, ESP: 0, END: 0, FOR: 0, INT: 0, SAG: 0, CHA: 0, DEX: 0, AGI: 0 },
    _statsModelV2: true,
    // Stats tertiaires : affinités / résistances élémentaires
    tertiaryStats: { affinityGlobal: 0, resistanceGlobal: 0 },
    tertiaryStatsAdded: { affinityGlobal: 0, resistanceGlobal: 0 },
    tertiaryStatPoints: 0,
    skill_points: 0,
    skills_unlocked: [],
    codexProgress: [],
    // Progression et titres vivent avec le personnage. Les anciens champs
    // homonymes a la racine sont migres dans migrateState puis supprimes.
    titles: { unlocked: [], active: null, unlockedAt: {} },
    progress: {
      kill: {}, damageTaken: {}, damageDealt: {}, heal: {}, questCompleted: {},
      duelWon: {}, duelLost: {}, eventParticipated: {}, civicInterventions: {},
      participatedEventIds: [], civicInterventionIds: [], stats: {}, level: 1,
      goldEarned: 0
    },
    location: [48.8566, 2.3522],
    preferences: { theme: 'fantasy', mapStyle: 'vintage', notifications: true },
    // Identité de départ (bloc de stats initial) — le multi-classing est mort,
    // remplacé par les builds nommés (buildManager.ts).
    activeClass: null,
    savedBuilds: []
  },
  quests: { active: [], completed: [] },
  activeQuest: null,
  equippedCosmetics: { familiar: null, shoesOverPants: null, pantsOverDress: null, ...LYRA_STARTER_OUTFIT },
  cosmeticTransforms: {},
  ownedCosmetics: [...LYRA_STARTER_ITEMS],
  playerEquippedCosmetics: { head: 'none', chest: 'none', legs: 'none', feet: 'none', main_hand: 'none', wings: 'none' },
  playerCosmeticTransforms: {},
  equipmentInventory: [] as EquipmentInstance[],
  equipmentMaxSlots: 20,
  // === Système d'équipement unifié V2 ===
  equipment: {
    hat: null,
    accessory: null,
    armor_top: null,
    armor_bottom: null,
    gloves: null,
    shoes: null,
    weapon_right: null,
    weapon_left: null,
    wings: null
  },
  // === Système Partenaire Lyra ===
  lyraPartner: {
    active: false,
    hiredAt: null,
    stats: {
      lyra_resurrections: 0,
      lyra_healed_total: 0,
      lyra_skills_used: 0,
      lyra_kills: 0,
      lyra_battles_together: 0,
      lyra_deaths: 0
    },
    unlockedOutfits: []
  },
  receptionist_stats: { name: 'Lyra Sunspark' },
  familiar_stats: { level: 1, xp: 0, hunger: 100, energy: 100, hygiene: 100, happiness: 100, form: 100, lastTick: Date.now(), isSleeping: false },
  familiar_upgrades: { premiumBrush: false, autoFeed: false, autoCare: false },
  combat: { active: false, player: null, enemy: null, allies: [], logs: [], isPlayerTurn: false, familiarCooldown: 0, turnEndTime: 0, opponentLevel: 1, selectedTargetId: 'enemy_main', pendingActors: [], duelMode: null, duelStake: 0 },
  huntEvents: [],
  activeEvents: [],
  activeDungeons: [],
  // Mémoire durable du registre : les identifiants sont classés du dernier
  // donjon croisé au plus ancien. Les notices restent, elles, autoritaires et
  // sont rechargées depuis le catalogue serveur à chaque session.
  encounteredDungeonIds: [] as string[],
  customContent: {},
  economy: { treasury: 0, dungeon_pool: 0, company_ca: 0, civic_pool: 0 },
  economyLog: [],
  dungeon: { activeRun: null, history: [], activeFloor: 0 },
  activeSOSAlert: null,
  sentSOSAlert: null,
  huntQuests: [],
  party: { id: null, leader: null, members: [], logs: [] },
  // Quêtes civiques : recharge individuelle renvoyée par le serveur. Les IDs
  // ne vont jamais dans completedQuestIds, car ces POI restent pour les autres.
  civicQuestCooldowns: {},
};

// Modèle d'attributs v2 : UN seul pool. Le pool « primaire » est retiré (=0) ;
// tout passe par `secondaryStatPoints` (= le pool d'attributs unique et libre).
export const STARTING_STAT_POINTS = 0;
export const STARTING_SKILL_POINTS = 1;
export const STAT_POINTS_PER_LEVEL = 0;
export const ATTRIBUTE_STARTING_POINTS = 5;
export const ATTRIBUTE_POINTS_PER_LEVEL = 4; // ancien : 1 (primaire) + 3 (secondaire)
export const SECONDARY_STAT_POINTS_PER_LEVEL = ATTRIBUTE_POINTS_PER_LEVEL;
export const TERTIARY_STAT_POINTS_PER_LEVEL = 1;
export const SKILL_POINTS_PER_LEVEL = 1;
// Ordre canonique des 9 attributs fondamentaux (modèle v2).
export const ATTRIBUTE_KEYS = ['VIT', 'ESP', 'END', 'FOR', 'DEX', 'INT', 'SAG', 'CHA', 'AGI'] as const;

// 1 point dans une affinité/résistance globale vaut GLOBAL_ELEMENT_RATIO dans chaque élément.

export function statPointsBudget(level = 1) {
  return STARTING_STAT_POINTS + Math.max(0, level - 1) * STAT_POINTS_PER_LEVEL;
}

export function secondaryStatPointsBudget(level = 1) {
  return ATTRIBUTE_STARTING_POINTS + Math.max(0, level - 1) * ATTRIBUTE_POINTS_PER_LEVEL;
}

export function tertiaryStatPointsBudget(level = 1) {
  return Math.max(0, level - 1) * TERTIARY_STAT_POINTS_PER_LEVEL;
}

export function skillPointsBudget(level = 1) {
  return STARTING_SKILL_POINTS + Math.max(0, level - 1) * SKILL_POINTS_PER_LEVEL;
}

export function questXpReward(quest: any) {
  const rankIndex = GUILD_RANKS.findIndex((definition) => definition.rank === quest?.rang);
  const mult = rankIndex >= 0 ? rankIndex + 1 : 1;
  return 25 + mult * 15;
}

function normalizeFamiliarStats(raw: any) {
  const fs = raw || {};
  const hunger = fs.hunger ?? 100;
  const energy = fs.energy ?? 100;
  const hygiene = fs.hygiene ?? fs.clean ?? 100;
  const happiness = fs.happiness ?? ((hunger + energy + hygiene) / 3);
  return {
    level: fs.level || 1,
    xp: fs.xp || 0,
    hunger,
    energy,
    hygiene,
    happiness: Math.max(0, Math.min(100, happiness)),
    form: fs.form ?? (hunger + energy + hygiene) / 3,
    lastTick: fs.lastTick ?? Date.now(),
    isSleeping: !!fs.isSleeping
  };
}

// RANKS/computeRank DOIVENT précéder migrateState : celui-ci s'exécute dès
// l'évaluation du module (loadState → reactive(state)) et recale le rang —
// déclarés plus bas, `const RANKS` serait en zone morte temporelle (TDZ).
export const RANKS = GUILD_RANKS.map(({ rank, minReputation, nextReputation, label, color }) => ({
  rang: rank,
  minXP: minReputation,
  nextXP: nextReputation,
  label: `Rang ${rank} - ${label}`,
  color,
}));

export function computeRank(xp: number) {
  const rank = rankForReputation(xp);
  return RANKS.find((definition) => definition.rang === rank.rank) || RANKS[0];
}

function mergeProgressMaps(primary: any, legacy: any) {
  const result: Record<string, number> = {};
  for (const source of [legacy, primary]) {
    if (!source || typeof source !== 'object' || Array.isArray(source)) continue;
    for (const [key, value] of Object.entries(source)) {
      const numeric = Math.max(0, Number(value) || 0);
      // Les deux emplacements ont parfois ete sauvegardes en miroir. Le max
      // conserve la progression sans la compter deux fois.
      result[key] = Math.max(result[key] || 0, numeric);
    }
  }
  return result;
}

function migratePlayerTitlesAndProgress(parsed: any) {
  const player = parsed.player;
  const legacyTitles = parsed.titles && typeof parsed.titles === 'object' ? parsed.titles : {};
  const playerTitles = player.titles && typeof player.titles === 'object' ? player.titles : {};
  const unlocked = [...new Set([
    ...(Array.isArray(legacyTitles.unlocked) ? legacyTitles.unlocked : []),
    ...(Array.isArray(playerTitles.unlocked) ? playerTitles.unlocked : [])
  ].filter((id) => typeof id === 'string' && id))];
  const requestedActive = playerTitles.active ?? legacyTitles.active ?? null;
  const legacyUnlockedAt = legacyTitles.unlockedAt && typeof legacyTitles.unlockedAt === 'object' ? legacyTitles.unlockedAt : {};
  const playerUnlockedAt = playerTitles.unlockedAt && typeof playerTitles.unlockedAt === 'object' ? playerTitles.unlockedAt : {};
  const unlockedAt: Record<string, number> = {};
  unlocked.forEach((id, index) => {
    unlockedAt[id] = Number(playerUnlockedAt[id] || legacyUnlockedAt[id]) || index + 1;
  });
  player.titles = {
    unlocked,
    active: requestedActive && unlocked.includes(requestedActive) ? requestedActive : null,
    unlockedAt
  };

  const defaults = DEFAULT_STATE.player.progress as Record<string, any>;
  const legacyProgress = parsed.progress && typeof parsed.progress === 'object' ? parsed.progress : {};
  const playerProgress = player.progress && typeof player.progress === 'object' ? player.progress : {};
  const progress: Record<string, any> = {};
  for (const key of ['kill', 'damageTaken', 'damageDealt', 'heal', 'questCompleted', 'duelWon', 'duelLost', 'eventParticipated', 'civicInterventions', 'stats']) {
    progress[key] = mergeProgressMaps(playerProgress[key], legacyProgress[key]);
  }
  progress.level = Math.max(Number(playerProgress.level) || 0, Number(legacyProgress.level) || 0, Number(player.level) || 1);
  progress.goldEarned = Math.max(Number(playerProgress.goldEarned) || 0, Number(legacyProgress.goldEarned) || 0);
  progress.participatedEventIds = [...new Set([
    ...(Array.isArray(legacyProgress.participatedEventIds) ? legacyProgress.participatedEventIds : []),
    ...(Array.isArray(playerProgress.participatedEventIds) ? playerProgress.participatedEventIds : [])
  ].filter(Boolean))];
  progress.civicInterventionIds = [...new Set([
    ...(Array.isArray(legacyProgress.civicInterventionIds) ? legacyProgress.civicInterventionIds : []),
    ...(Array.isArray(playerProgress.civicInterventionIds) ? playerProgress.civicInterventionIds : [])
  ].filter(Boolean))];
  for (const [key, value] of Object.entries(defaults)) {
    if (progress[key] === undefined) progress[key] = JSON.parse(JSON.stringify(value));
  }
  player.progress = progress;

  // Les bonus de titre sont maintenant derives du seul titre actif. Les
  // anciens cumuls persistes etaient non idempotents et ne doivent plus servir.
  delete player.titleStats;
  delete player.titleEffects;
  delete parsed.titles;
  delete parsed.progress;
  delete parsed.titleStats;
  delete parsed.titleEffects;
}

export function migrateState(parsed: any) {
  if (!parsed.inventory) parsed.inventory = { ...DEFAULT_STATE.inventory };
  if (!parsed.inventory.items) parsed.inventory.items = [];
  // Inventaire PROPRE (retour user vague 2) : les anciennes saves empilaient un
  // objet par achat (10× « Potion de Soin »). On fusionne les doublons par id en
  // une seule pile avec la quantité réelle.
  if (Array.isArray(parsed.inventory.items)) {
    const merged: Record<string, any> = {};
    for (const it of parsed.inventory.items) {
      if (!it || it.id == null) continue;
      // Les lots procéduraux ne fusionnent que si niveau, rareté, étoiles et
      // propriétés ont produit la même stackKey. Les objets legacy restent par id.
      const inventoryKey = it.stackKey || it.batch?.stackKey || it.id;
      if (merged[inventoryKey]) merged[inventoryKey].qty = (merged[inventoryKey].qty || 1) + (it.qty || 1);
      else merged[inventoryKey] = { ...it, stackKey: it.batch?.stackKey || it.stackKey, qty: it.qty || 1 };
    }
    parsed.inventory.items = Object.values(merged);
  }
  migrateConsumableInventoryState(parsed);
  if (!parsed.equippedCosmetics) parsed.equippedCosmetics = { ...DEFAULT_STATE.equippedCosmetics };
  // Répare uniquement les slots Lyra jamais initialisés. Une sélection réelle
  // du joueur reste intacte ; les copies aventurier sont nettoyées plus bas.
  for (const [slot, itemId] of Object.entries(LYRA_STARTER_OUTFIT)) {
    if (!itemId) continue;
    const current = parsed.equippedCosmetics[slot];
    if (current === undefined || current === 'none') parsed.equippedCosmetics[slot] = itemId;
  }
  // Une pièce temporairement retirée du dressing ne doit pas rester coincée
  // sur Lyra dans une ancienne sauvegarde. Sa possession est conservée.
  for (const [slot, itemId] of Object.entries(parsed.equippedCosmetics)) {
    if (!TEMPORARILY_HIDDEN_LYRA_ITEM_IDS.has(String(itemId))) continue;
    parsed.equippedCosmetics[slot] = LYRA_STARTER_OUTFIT[slot] || 'none';
  }
  if (parsed.equippedCosmetics.shoesOverPants === undefined) parsed.equippedCosmetics.shoesOverPants = null;
  if (parsed.equippedCosmetics.pantsOverDress === undefined) parsed.equippedCosmetics.pantsOverDress = null;
  if (!parsed.cosmeticTransforms) parsed.cosmeticTransforms = {};
  if (!parsed.ownedCosmetics) parsed.ownedCosmetics = [];
  parsed.ownedCosmetics = parsed.ownedCosmetics.filter((itemId: unknown) => !RETIRED_LYRA_DERIVED_ADVENTURER_IDS.has(String(itemId)));
  for (const itemId of RETIRED_LYRA_DERIVED_ADVENTURER_IDS) delete parsed.cosmeticTransforms[itemId];
  const lyraWardrobeIds = [
    'village_top', 'village_pants', 'village_shoes', 'receptionist_red_hair', 'receptionist_glasses', 'quest_clipboard',
    'receptionist_top', 'receptionist_pants', 'receptionist_shoes', 'receptionist_barrette', 'receptionist_necklace',
    'receptionist_black_hair', 'village_barrette',
    'fairy_dress', 'fairy_shoes', 'auburn_hair', 'emerald_necklace', 'white_flower', 'fairy_wings',
  ];
  for (const itemId of lyraWardrobeIds) {
    if (!parsed.ownedCosmetics.includes(itemId)) parsed.ownedCosmetics.push(itemId);
  }
  if (!parsed.receptionist_stats) parsed.receptionist_stats = { ...DEFAULT_STATE.receptionist_stats };

  if (!parsed.familiar_stats) parsed.familiar_stats = { ...DEFAULT_STATE.familiar_stats };
  parsed.familiar_stats = normalizeFamiliarStats(parsed.familiar_stats);
  if (!parsed.familiar_upgrades) parsed.familiar_upgrades = { ...DEFAULT_STATE.familiar_upgrades };
  if (!parsed.combat) parsed.combat = { ...DEFAULT_STATE.combat };
  if (!parsed.player) parsed.player = { ...DEFAULT_STATE.player };
  if (!parsed.player.location) parsed.player.location = [48.8566, 2.3522];
  // P7 (équilibrage) — migration des saves : estime l'XP cumulée depuis les
  // niveaux déjà passés (ancienne courbe linéaire niveau×100) + le résiduel.
  if (parsed.player.xpTotal == null) {
    const lvl = parsed.player.level || 1;
    parsed.player.xpTotal = (100 * lvl * (lvl - 1)) / 2 + (parsed.player.xp || 0);
  }
  // Rang DÉRIVÉ de la RÉPUTATION d'entraide (maj-05 §3.7 — durcissement).
  // La réputation est SERVEUR-AUTORITAIRE (reputationStore) : plus de grandfather
  // depuis xpTotal (le combat ne donne pas le rang). Un client sans réputation
  // démarre à 0 ; le serveur imposera la vraie valeur (registre) au chargement
  // (getUser) et à la sauvegarde. Le rang local n'est qu'un affichage optimiste.
  if (parsed.player.reputation == null) parsed.player.reputation = 0;
  parsed.player.rang = computeRank(parsed.player.reputation).rang;
  if (!isGuildRank(parsed.player.certificationRank)) {
    parsed.player.certificationRank = minimumCertificationForLevel(parsed.player.level || 1);
  }
  parsed.player.level = Math.max(1, Math.min(PLAYER_LEVEL_MAX, Number(parsed.player.level) || 1));
  parsed.player.xp = Math.max(0, Number(parsed.player.xp) || 0);

  // --- Migration primaires / secondaires / tertiaires ---
  if (!parsed.player.stats) parsed.player.stats = { ...DEFAULT_STATE.player.stats };
  if (!parsed.player.stats_added) parsed.player.stats_added = { ...DEFAULT_STATE.player.stats_added };
  for (const k of ['PV', 'ARMURE', 'RM', 'VIT']) {
    if (parsed.player.stats[k] === undefined) parsed.player.stats[k] = (DEFAULT_STATE.player.stats as Record<string, number>)[k];
    if (parsed.player.stats_added[k] === undefined) parsed.player.stats_added[k] = 0;
  }

  // Ancien format : stats secondaires mélangeaient primaires/secondaires
  if (parsed.player.stats.FOR !== undefined || parsed.player.stats.INT !== undefined) {
    if (!parsed.player.secondaryStats) parsed.player.secondaryStats = { ...DEFAULT_STATE.player.secondaryStats };
    for (const k of ['FOR', 'INT', 'SAG', 'CHA', 'DEX', 'AGI']) {
      if (parsed.player.secondaryStats[k] === undefined) {
        parsed.player.secondaryStats[k] = parsed.player.stats[k] ?? (DEFAULT_STATE.player.secondaryStats as Record<string, number>)[k];
      }
      delete parsed.player.stats[k];
    }
    // Migrations des points alloués anciens vers la nouvelle structure
    if (parsed.player.stats_added) {
      for (const k of ['FOR', 'INT', 'SAG', 'CHA', 'DEX', 'AGI']) {
        if (parsed.player.stats_added[k]) {
          if (!parsed.player.secondaryStatsAdded) parsed.player.secondaryStatsAdded = {};
          parsed.player.secondaryStatsAdded[k] = (parsed.player.secondaryStatsAdded[k] || 0) + parsed.player.stats_added[k];
          delete parsed.player.stats_added[k];
        }
      }
    }
  }

  if (!parsed.player.secondaryStats) parsed.player.secondaryStats = { ...DEFAULT_STATE.player.secondaryStats };
  if (!parsed.player.secondaryStatsAdded) parsed.player.secondaryStatsAdded = { ...DEFAULT_STATE.player.secondaryStatsAdded };
  for (const k of ['FOR', 'INT', 'SAG', 'CHA', 'DEX', 'AGI']) {
    if (parsed.player.secondaryStats[k] === undefined) parsed.player.secondaryStats[k] = (DEFAULT_STATE.player.secondaryStats as Record<string, number>)[k];
    if (parsed.player.secondaryStatsAdded[k] === undefined) parsed.player.secondaryStatsAdded[k] = 0;
  }

  // Conversion de l'ancien système secondaire RES/ESQ/CRIT/PRE → remboursement des points
  if (parsed.player.secondaryStatsAdded && parsed.player.secondaryStatsAdded.RES !== undefined) {
    const legacyTotal = ['RES', 'ESQ', 'CRIT', 'PRE'].reduce((s, k) => s + (parsed.player.secondaryStatsAdded[k] || 0), 0);
    parsed.player.secondaryStatPoints = (parsed.player.secondaryStatPoints || 0) + legacyTotal;
    parsed.player.secondaryStatsAdded = { ...DEFAULT_STATE.player.secondaryStatsAdded };
  }

  if (parsed.player.stat_points != null && parsed.player.statPoints == null) {
    parsed.player.statPoints = parsed.player.stat_points;
  }
  if (parsed.player.statPoints === undefined) parsed.player.statPoints = 0;
  if (parsed.player.secondaryStatPoints === undefined) parsed.player.secondaryStatPoints = 0;

  // Stats tertiaires
  if (!parsed.player.tertiaryStats) parsed.player.tertiaryStats = { ...DEFAULT_STATE.player.tertiaryStats };
  if (!parsed.player.tertiaryStatsAdded) parsed.player.tertiaryStatsAdded = { ...DEFAULT_STATE.player.tertiaryStatsAdded };
  if (parsed.player.tertiaryStatPoints === undefined) parsed.player.tertiaryStatPoints = 0;

  // --- Modèle d'attributs v2 : pool unique + Esprit (mana dédié) ---
  // Étend la carte d'attributs aux 9 clés (ajoute VIT, ESP, END au pool libre).
  for (const k of ['VIT', 'ESP', 'END', 'FOR', 'INT', 'SAG', 'CHA', 'DEX', 'AGI']) {
    if (parsed.player.secondaryStats[k] === undefined) parsed.player.secondaryStats[k] = 10;
    if (parsed.player.secondaryStatsAdded[k] === undefined) parsed.player.secondaryStatsAdded[k] = 0;
  }
  // Respec UNIQUE : la croissance forcée par classe disparaît. On rend TOUS les
  // points (primaires + secondaires) dans le pool unique, à réallouer librement.
  if (!parsed.player._statsModelV2) {
    const lvl = parsed.player.level || 1;
    parsed.player.secondaryStatPoints = secondaryStatPointsBudget(lvl);
    parsed.player.statPoints = 0;
    for (const k of ['VIT', 'ESP', 'END', 'FOR', 'INT', 'SAG', 'CHA', 'DEX', 'AGI']) {
      parsed.player.secondaryStatsAdded[k] = 0;
    }
    parsed.player.stats_added = { PV: 0, ARMURE: 0, RM: 0, VIT: 0 };
    parsed.player._statsModelV2 = true;
  }

  if (!parsed.player.skills_unlocked) parsed.player.skills_unlocked = [];
  if (!parsed.player.codexProgress) parsed.player.codexProgress = [];
  migratePlayerTitlesAndProgress(parsed);
  // --- Builds nommés (remplacent le multi-classing, 2026-07-27) ---
  if (!parsed.player.activeClass) parsed.player.activeClass = parsed.player.classe || null;
  if (!Array.isArray(parsed.player.savedBuilds)) parsed.player.savedBuilds = [];
  delete parsed.player.purchasedClasses;
  delete parsed.player.classSnapshots;
  if (!parsed.economy) parsed.economy = { ...DEFAULT_STATE.economy };
  if (parsed.economy.civic_pool == null) parsed.economy.civic_pool = 0;
  if (parsed.economy.dungeon_pool == null) parsed.economy.dungeon_pool = 0;
  if (!parsed.dungeon) parsed.dungeon = { ...DEFAULT_STATE.dungeon };
  if (!parsed.wallet) parsed.wallet = { ...DEFAULT_STATE.wallet };
  if (parsed.wallet.totalWithdrawn == null) parsed.wallet.totalWithdrawn = 0;
  if (!Array.isArray(parsed.rencontres)) parsed.rencontres = [];
  if (!parsed.sponsorGifts || typeof parsed.sponsorGifts !== 'object') parsed.sponsorGifts = {};
  // cuivres_bonus fusionné dans cuivres (28/07) : monnaie unique. On récupère la
  // valeur des anciens saves (aucune confiscation) puis on retire le champ.
  if (parsed.wallet.cuivres_bonus) parsed.wallet.cuivres = (parsed.wallet.cuivres || 0) + parsed.wallet.cuivres_bonus;
  delete parsed.wallet.cuivres_bonus;
  // MAJ-03 B1 : monnaie premium « Essence » (achetée en €, jamais gagnée en jeu)
  if (parsed.wallet.essence == null) parsed.wallet.essence = 0;
  if (!parsed.economyLog) parsed.economyLog = [];
  if (parsed.activeSOSAlert === undefined) parsed.activeSOSAlert = null;
  if (parsed.sentSOSAlert === undefined) parsed.sentSOSAlert = null;
  if (!parsed.huntQuests) parsed.huntQuests = [];
  if (!parsed.activeEvents) parsed.activeEvents = [];
  if (!parsed.activeDungeons) parsed.activeDungeons = [];
  if (!Array.isArray(parsed.encounteredDungeonIds)) parsed.encounteredDungeonIds = [];
  parsed.encounteredDungeonIds = [...new Set(
    parsed.encounteredDungeonIds
      .filter((id: unknown): id is string => typeof id === 'string' && id.length > 0)
      .map((id: string) => id.slice(0, 160)),
  )].slice(0, 500);
  if (!parsed.party) parsed.party = { ...DEFAULT_STATE.party };
  // Retrait des anciens mercenaires : les sauvegardes existantes peuvent
  // encore contenir leurs followers et membres d'escouade persistés.
  delete parsed.player.followers;
  if (Array.isArray(parsed.party.members)) {
    parsed.party.members = parsed.party.members.filter((member: any) => !member?.isBot);
    if (!parsed.party.members.length) {
      parsed.party.id = null;
      parsed.party.leader = null;
    }
  }
  if (parsed.activeEvent) {
    parsed.activeEvents.push(parsed.activeEvent);
    delete parsed.activeEvent;
  }
  if (!parsed.huntEvents) parsed.huntEvents = [];
  if (parsed.huntEvent) {
    parsed.huntEvents.push(parsed.huntEvent);
    delete parsed.huntEvent;
  }
  if (!parsed.customContent) parsed.customContent = {};
  if (!parsed.customContent.classes) parsed.customContent.classes = {};
  if (!parsed.customContent.items) parsed.customContent.items = {};

  for (const itemId of RETIRED_LYRA_DERIVED_ADVENTURER_IDS) delete parsed.customContent.items[itemId];

  // Supprime les overrides d'items corrompus (sans catégorie valide)
  // pour éviter qu'ils écrasent des items de base correctement définis.
  for (const [itemId, item] of Object.entries<any>(parsed.customContent.items)) {
    if (!item || typeof item !== 'object') {
      delete parsed.customContent.items[itemId];
      continue;
    }
    if (item.cat === undefined || item.cat === null || item.cat === '') {
      console.log('[MIGRATE] Removing invalid custom item override without cat:', itemId);
      delete parsed.customContent.items[itemId];
    }
  }
  if (!parsed.customContent.skillTrees) parsed.customContent.skillTrees = {};
  if (!parsed.customContent.titles) parsed.customContent.titles = {};
  if (!parsed.customContent.familiarSkills) parsed.customContent.familiarSkills = {};
  if (!parsed.customContent.statusEffects) parsed.customContent.statusEffects = {};
  if (!parsed.customContent.elements) parsed.customContent.elements = {};
  if (!parsed.completedQuestIds) {
    parsed.completedQuestIds = (parsed.quests?.completed || [])
      .map((q: any) => (typeof q === 'object' ? q.id : q))
      .filter(Boolean);
  }
  if (parsed.combat?.active) {
    parsed.combat = { ...DEFAULT_STATE.combat };
  }

  // Clean up relic event hunt quests (event monsters are now server-managed with direct rewards)
  if (parsed.huntQuests?.length) {
    const before = parsed.huntQuests.length;
    parsed.huntQuests = parsed.huntQuests.filter((q: any) => !q.eventId);
    if (parsed.huntQuests.length < before) {
      console.log('[MIGRATE] Removed', before - parsed.huntQuests.length, 'event hunt quest relics');
    }
  }

  // Clean up stale local hunt events (server-managed events are now authoritative)
  if (parsed.huntEvents?.length) {
    const before = parsed.huntEvents.length;
    parsed.huntEvents = parsed.huntEvents.filter((h: any) => {
      // Keep only recent local events (last 5 minutes) when offline fallback matters
      return h.eventId && h.launchedAt && (Date.now() - h.launchedAt < 5 * 60 * 1000);
    });
    if (parsed.huntEvents.length < before) {
      console.log('[MIGRATE] Removed', before - parsed.huntEvents.length, 'stale local hunt events');
    }
  }

  // === MIGRATION ÉQUIPEMENT UNIFIÉ V2 ===
  if (!Array.isArray(parsed.equipmentInventory)) parsed.equipmentInventory = [];
  if (!Number.isFinite(Number(parsed.equipmentMaxSlots)) || Number(parsed.equipmentMaxSlots) < 1) parsed.equipmentMaxSlots = 20;

  // Taille de pile : valeur de compte, extensible chez le Marchand. Le serveur
  // reste autoritaire, cette migration ne fait qu'éviter une lecture indéfinie.
  if (parsed.inventory && typeof parsed.inventory === 'object') {
    const stackSize = Number(parsed.inventory.stackSize);
    if (!Number.isFinite(stackSize) || stackSize < 10) parsed.inventory.stackSize = 10;
  }
  if (!parsed.equipment) {
    parsed.equipment = { hat: null, accessory: null, armor_top: null, armor_bottom: null, gloves: null, shoes: null, weapon_right: null, weapon_left: null, wings: null };
    // Migrer depuis playerEquippedCosmetics (ancien système aventurier)
    const pec = parsed.playerEquippedCosmetics;
    if (pec) {
      if (pec.head && pec.head !== 'none') parsed.equipment.hat = pec.head;
      if (pec.chest && pec.chest !== 'none') parsed.equipment.armor_top = pec.chest;
      if (pec.legs && pec.legs !== 'none') parsed.equipment.armor_bottom = pec.legs;
      if (pec.feet && pec.feet !== 'none') parsed.equipment.shoes = pec.feet;
      if (pec.main_hand && pec.main_hand !== 'none') parsed.equipment.weapon_right = pec.main_hand;
      if (pec.wings && pec.wings !== 'none') parsed.equipment.wings = pec.wings;
    }
    console.log('[MIGRATE] Equipment V2 initialized from legacy data:', parsed.equipment);
  }
  const retiredEquipmentInstances = new Set(
    parsed.equipmentInventory
      .filter((entry: any) => RETIRED_LYRA_DERIVED_ADVENTURER_IDS.has(String(entry?.templateId || entry?.itemId || entry?.id)))
      .map((entry: any) => String(entry.instanceId || ''))
      .filter(Boolean),
  );
  // Assurer tous les slots
  for (const slot of ['hat', 'accessory', 'armor_top', 'armor_bottom', 'gloves', 'shoes', 'weapon_right', 'weapon_left', 'wings']) {
    if (parsed.equipment[slot] === undefined) parsed.equipment[slot] = null;
    if (RETIRED_LYRA_DERIVED_ADVENTURER_IDS.has(String(parsed.equipment[slot]))
      || retiredEquipmentInstances.has(String(parsed.equipment[slot]))) parsed.equipment[slot] = null;
  }
  parsed.equipmentInventory = parsed.equipmentInventory.filter((entry: any) => (
    entry && !RETIRED_LYRA_DERIVED_ADVENTURER_IDS.has(String(entry.templateId || entry.itemId || entry.id))
  ));
  parsed.inventory.items = parsed.inventory.items.filter((entry: any) => (
    entry && !RETIRED_LYRA_DERIVED_ADVENTURER_IDS.has(String(entry.templateId || entry.itemId || entry.id))
  ));
  for (const build of parsed.player?.savedBuilds || []) {
    if (!build?.equipment || typeof build.equipment !== 'object') continue;
    for (const [slot, reference] of Object.entries(build.equipment)) {
      if (RETIRED_LYRA_DERIVED_ADVENTURER_IDS.has(String(reference))
        || retiredEquipmentInstances.has(String(reference))) build.equipment[slot] = null;
    }
  }
  migrateLegacyEquipmentState(parsed, parsed.deviceId || 'legacy-local', BASE_ITEMS);

  // === NETTOYAGE LEGACY : les slots équipement sont maintenant en V2 ===
  // On garde uniquement : familiar (pas en V2) et les préférences de z-order.
  if (parsed.playerEquippedCosmetics) {
    const keep: Record<string, unknown> = {};
    if (parsed.playerEquippedCosmetics.legsOverChest !== undefined) {
      keep.legsOverChest = parsed.playerEquippedCosmetics.legsOverChest;
    }
    parsed.playerEquippedCosmetics = keep;
  }
  // equippedCosmetics reste la source de vérité du dressing de Lyra.

  // === MIGRATION LYRA PARTENAIRE ===
  if (!parsed.lyraPartner) {
    parsed.lyraPartner = { ...DEFAULT_STATE.lyraPartner, stats: { ...DEFAULT_STATE.lyraPartner.stats }, unlockedOutfits: [] };
  }
  if (!parsed.lyraPartner.stats) parsed.lyraPartner.stats = { ...DEFAULT_STATE.lyraPartner.stats };
  if (!parsed.lyraPartner.unlockedOutfits) parsed.lyraPartner.unlockedOutfits = [];

  return parsed;
}

export function getDefaultState() {
  return JSON.parse(JSON.stringify(DEFAULT_STATE));
}

/**
 * Merges source state into the target state in-place, preserving object/array
 * references so Vue reactivity and external const references (e.g. party) stay valid.
 * Arrays are replaced by content; nested objects are merged recursively.
 */
export function mergeStateInto(target: any, source: any) {
  if (!target || !source || typeof source !== 'object') return target;
  for (const key of Object.keys(source)) {
    const srcVal = source[key];
    const tgtVal = target[key];
    if (srcVal && typeof srcVal === 'object') {
      if (Array.isArray(srcVal)) {
        if (!Array.isArray(tgtVal)) {
          target[key] = srcVal.slice();
        } else {
          tgtVal.length = 0;
          tgtVal.push(...srcVal);
        }
      } else if (tgtVal && typeof tgtVal === 'object' && !Array.isArray(tgtVal)) {
        mergeStateInto(tgtVal, srcVal);
      } else {
        target[key] = srcVal;
      }
    } else {
      target[key] = srcVal;
    }
  }
  return target;
}

/**
 * Remplace exactement un etat tout en conservant les references reactives deja
 * exposees par les stores (notamment `party`). Contrairement a mergeStateInto,
 * les cles absentes de la source sont retirees : indispensable au changement
 * de compte pour ne garder aucun reliquat du compte precedent.
 */
export function replaceStateInto(target: any, source: any) {
  if (!target || !source || typeof source !== 'object') return target;
  for (const key of Object.keys(target)) {
    if (!Object.prototype.hasOwnProperty.call(source, key)) delete target[key];
  }
  for (const key of Object.keys(source)) {
    const srcVal = source[key];
    const tgtVal = target[key];
    if (Array.isArray(srcVal)) {
      if (Array.isArray(tgtVal)) {
        tgtVal.length = 0;
        tgtVal.push(...srcVal);
      } else {
        target[key] = srcVal.slice();
      }
    } else if (srcVal && typeof srcVal === 'object') {
      if (tgtVal && typeof tgtVal === 'object' && !Array.isArray(tgtVal)) {
        replaceStateInto(tgtVal, srcVal);
      } else {
        target[key] = srcVal;
      }
    } else {
      target[key] = srcVal;
    }
  }
  return target;
}

/** Cle historique globale, conservee uniquement pour une migration prudente. */
export const STATE_STORAGE_KEY = 'guild_state_v2';

export function accountStateStorageKey(accountId: string) {
  return accountStorageKey(STATE_STORAGE_KEY, accountId);
}

export function hasCharacterData(gameState = state) {
  return !!(
    gameState?.player?.name &&
    gameState.player.name !== 'Aventurier' &&
    (gameState.player.activeClass || gameState.player.classe)
  );
}

/** Prepare une sauvegarde canonique recue du serveur pour son usage en memoire. */
export function hydrateStatePublicAssetUrls<T>(raw: T): T {
  return resolvePublicAssetUrls(raw);
}

function sanitizeStateForPersist(raw: any) {
  const copy = migrateState(JSON.parse(JSON.stringify(raw)));
  // Les URL absolues du build ne sont qu'une representation d'execution. La
  // sauvegarde conserve des chemins canoniques pour ne jamais graver un CDN ou
  // un domaine dans le profil du joueur.
  canonicalizePublicAssetUrls(copy);
  const accountId = String(copy.deviceId || storedAccountId() || '');
  if (accountId) {
    copy.deviceId = accountId;
    copy._accountId = accountId;
  } else {
    delete copy.deviceId;
    delete copy._accountId;
  }
  // Les alertes SOS sont volatiles : ne pas les faire survivre au refresh
  copy.activeSOSAlert = null;
  copy.sentSOSAlert = null;
  // Identité de session (rôles admin/staff, email) : JAMAIS dans la sauvegarde
  // de jeu. Ces valeurs vivent dans leurs clés localStorage dédiées et sont
  // fournies par le serveur au login/bind. Les persister ici faisait qu'une
  // vieille sauvegarde (écrite en joueur normal) écrasait les rôles fraîchement
  // reçus à chaque syncFromServer — UI admin qui « flashe » puis disparaît.
  delete copy.accountRoles;
  delete copy.accountEmail;
  // Ancien prototype : ce booléen client permettait de simuler le statut
  // mandataire. Il ne doit plus voyager dans les sauvegardes ni entre appareils.
  if (copy.player) delete copy.player.isMandataire;
  // Les donjons actifs APPARTIENNENT au serveur : `dungeon2:list` les renvoie à
  // chaque connexion. Les sauvegarder faisait survivre à l'infini des donjons
  // supprimés — marqueurs fantômes sur la carte, proposant une entrée morte —
  // et alourdissait chaque sauvegarde de compte d'une copie de données que le
  // serveur possède déjà. `mergeStateInto` n'écrase que les clés présentes :
  // leur absence ici ne vide donc jamais la liste en cours de session.
  delete copy.activeDungeons;
  copy._persistedAt = Date.now();
  return copy;
}

export function serializeStateForServer() {
  return sanitizeStateForPersist(state);
}

export function persistStateToLocal() {
  try {
    const accountId = String(state.deviceId || storedAccountId() || '');
    if (!accountId || isAccountPersistenceBlocked(accountId)) return;
    const payload = sanitizeStateForPersist(state);
    writeAccountItem(STATE_STORAGE_KEY, accountId, JSON.stringify(payload));
  } catch (err) {
    console.warn('Sauvegarde locale impossible:', err);
  }
}

function prepareLoadedState(saved: any, accountId: string) {
  const initialState = saved || getDefaultState();
  // Operation inverse de sanitizeStateForPersist : en memoire, les composants
  // historiques peuvent continuer a utiliser directement leurs champs img.
  hydrateStatePublicAssetUrls(initialState);
  if (accountId) {
    initialState.deviceId = accountId;
    initialState._accountId = accountId;
  }

  if (initialState.combat?.active) initialState.combat = { ...DEFAULT_STATE.combat };
  initialState.activeSOSAlert = null;
  initialState.sentSOSAlert = null;
  if (initialState.player) delete initialState.player.isMandataire;
  return initialState;
}

export function loadLocalStateForAccount(accountId: string) {
  if (!accountId) return prepareLoadedState(getDefaultState(), '');
  let saved = null;

  try {
    let raw = readAccountItem(STATE_STORAGE_KEY, accountId);
    // Migration de l'ancienne cle globale seulement si le blob nomme lui-meme
    // ce compte. Un blob sans proprietaire reste en place mais n'est attribue a
    // personne : mieux vaut une archive locale qu'une progression transferee.
    if (!raw) {
      const legacyRaw = localStorage.getItem(STATE_STORAGE_KEY);
      if (legacyRaw) {
        const legacy = JSON.parse(legacyRaw);
        const legacyOwner = String(legacy?._accountId || legacy?.deviceId || '');
        if (legacyOwner === accountId) {
          raw = legacyRaw;
          writeAccountItem(STATE_STORAGE_KEY, accountId, legacyRaw);
        }
      }
    }
    if (raw) saved = migrateState(JSON.parse(raw));
  } catch (err) {
    console.warn('État local corrompu, réinitialisation:', err);
  }

  return prepareLoadedState(saved, accountId);
}

function loadState() {
  return loadLocalStateForAccount(storedAccountId());
}

export const state = reactive(loadState());

let titleCheckQueued = false;
export function queueTitleCheck() {
  if (titleCheckQueued) return;
  titleCheckQueued = true;
  queueMicrotask(() => {
    titleCheckQueued = false;
    import('../domain/progression/progressTracker.js')
      .then(({ checkAllTitles }) => checkAllTitles())
      .catch((err) => console.warn('Verification des titres impossible:', err));
  });
}

export function recordGoldEarned(amount: number) {
  if (!state.player || !Number.isFinite(amount) || amount <= 0) return;
  if (!state.player.progress) state.player.progress = JSON.parse(JSON.stringify(DEFAULT_STATE.player.progress));
  state.player.progress.goldEarned = (state.player.progress.goldEarned || 0) + amount;
  queueTitleCheck();
}

initContentRegistry(state);
setContentRegistries(REGISTRIES);
setElementsRegistry(REGISTRIES);
setStatusEffectsRegistry(REGISTRIES);

// Répare aussi les sauvegardes qui satisfaisaient déjà une condition mais dont
// le titre n'avait jamais été attribué (ancien bug ou synchronisation serveur).
queueTitleCheck();

// Persiste immédiatement l'état nettoyé par migrateState (suppression des custom items corrompus)
// afin de nettoyer le localStorage dès le premier chargement.
try { persistStateToLocal(); } catch {}

export { socket, serverOnline };

export function addCuivres(amount: number, desc = '', countsAsEarned = amount > 0) {
  state.wallet.cuivres += amount;
  state.wallet.transactions.unshift({ id: Date.now(), date: new Date().toISOString(), desc, amount, type: amount > 0 ? 'earn' : 'spend' });
  if (countsAsEarned && amount > 0) recordGoldEarned(amount);
}

export { xpNeededForLevel };

/** Rencontres NFC — ajoute/incrémente une entrée du carnet. Retourne l'entrée. */
export function addRencontreToCarnet(withId: string, withName?: string) {
  if (!withId) return null;
  if (!Array.isArray(state.rencontres)) state.rencontres = [];
  let entry = state.rencontres.find((r: any) => r.accountId === withId);
  if (entry) {
    entry.count = (entry.count || 1) + 1;
    entry.lastAt = Date.now();
    if (withName) entry.nom = withName;
  } else {
    entry = { accountId: withId, nom: withName || 'Aventurier', count: 1, lastAt: Date.now() };
    state.rencontres.unshift(entry);
  }
  return entry;
}

export function addXp(amount: number, source = '') {
  if (!amount || !state.player) return;
  const requested = Math.max(0, Math.floor(Number(amount) || 0));
  if (!requested) return;
  const certificationRank = state.player.certificationRank || minimumCertificationForLevel(state.player.level || 1);
  state.player.certificationRank = certificationRank;
  const levelCap = certificationMaxLevel(certificationRank);
  if (state.player.level >= levelCap) {
    const currentLimit = state.player.level >= PLAYER_LEVEL_MAX ? 0 : xpNeededForLevel(state.player.level);
    state.player.xp = Math.min(Math.max(0, state.player.xp || 0), currentLimit);
  }
  state.player.xp += requested;
  // XP CUMULÉE : plafonne le NIVEAU côté serveur (progressionGuard). Jamais
  // décrémentée. Depuis maj-05 §3.7 elle ne sert PLUS au rang (voir addReputation).
  state.player.xpTotal = (state.player.xpTotal || 0) + requested;
  let leveled = false;
  // Points gagnés cumulés sur l'ensemble des niveaux pris dans cet appel — pour
  // les afficher au joueur (il gagnait des points sans le voir → « impression »
  // de ne rien toucher).
  let gainedAttr = 0, gainedElem = 0, gainedSkill = 0;

  while (state.player.level < levelCap && state.player.level < PLAYER_LEVEL_MAX) {
    const xpNeeded = xpNeededForLevel(state.player.level);
    if (state.player.xp < xpNeeded) break;
    state.player.xp -= xpNeeded;
    state.player.level++;
    state.player.statPoints = (state.player.statPoints || 0) + STAT_POINTS_PER_LEVEL;
    state.player.secondaryStatPoints = (state.player.secondaryStatPoints || 0) + SECONDARY_STAT_POINTS_PER_LEVEL;
    state.player.tertiaryStatPoints = (state.player.tertiaryStatPoints || 0) + TERTIARY_STAT_POINTS_PER_LEVEL;
    state.player.skill_points = (state.player.skill_points || 0) + SKILL_POINTS_PER_LEVEL;
    if (!state.player.progress) state.player.progress = JSON.parse(JSON.stringify(DEFAULT_STATE.player.progress));
    state.player.progress.level = state.player.level;
    gainedAttr += SECONDARY_STAT_POINTS_PER_LEVEL;
    gainedElem += TERTIARY_STAT_POINTS_PER_LEVEL;
    gainedSkill += SKILL_POINTS_PER_LEVEL;
    checkCodexUnlock(`level:${state.player.level}`);
    leveled = true;
  }

  if (state.player.level >= levelCap) {
    const cap = state.player.level >= PLAYER_LEVEL_MAX ? 0 : xpNeededForLevel(state.player.level);
    const discarded = Math.max(0, state.player.xp - cap);
    state.player.xp = Math.min(state.player.xp, cap);
    state.player.xpTotal = Math.max(0, state.player.xpTotal - discarded);
  }

  // maj-05 §3.7 : le COMBAT ne touche plus le rang (rang = réputation d'entraide,
  // dérivée dans addReputation). addXp ne gère plus que le NIVEAU du personnage.

  if (leveled) {
    queueTitleCheck();
    // On liste précisément les points à répartir, sinon le joueur ne sait pas
    // qu'il en a gagné (attributs, affinités/résistances, sorts).
    const parts: string[] = [];
    if (gainedAttr) parts.push(`+${gainedAttr} attribut${gainedAttr > 1 ? 's' : ''}`);
    if (gainedElem) parts.push(`+${gainedElem} affinité/résistance`);
    if (gainedSkill) parts.push(`+${gainedSkill} sort${gainedSkill > 1 ? 's' : ''}`);
    const gains = parts.length ? ` — ${parts.join(', ')} à répartir !` : '';
    window.dispatchEvent(new CustomEvent('guild-toast', {
      detail: { msg: `Niveau Supérieur ! Level ${state.player.level}${gains}`, type: 'success' }
    }));
  }
}

/**
 * Réputation de Guilde (maj-05 §3.7) : SEULE source du RANG. Alimentée uniquement
 * par l'entraide (quêtes, Sceau de gratitude, SOS) — jamais par le combat. Le rang
 * redevient une récompense d'entraide, conforme au GDD.
 */
export function addReputation(amount: number, source = '') {
  if (!amount || !state.player) return;
  state.player.reputation = (state.player.reputation || 0) + amount;
  const prevRang = state.player.rang;
  state.player.rang = computeRank(state.player.reputation).rang;
  if (state.player.rang !== prevRang) {
    window.dispatchEvent(new CustomEvent('guild-toast', {
      detail: { msg: `Rang de Guilde : ${state.player.rang} ! (réputation d'entraide)`, type: 'success' }
    }));
  }
}

export function applyDefeatXpPenalty() {
  if (!state.player) return 0;
  // P7 : la pénalité touche l'XP de progression du niveau, PAS xpTotal —
  // le rang acquis ne redescend jamais.
  const xpNeeded = xpNeededForLevel(state.player.level);
  const penalty = Math.floor(xpNeeded * 0.10);
  const actualPenalty = Math.min(state.player.xp, penalty);
  state.player.xp = Math.max(0, state.player.xp - penalty);
  return actualPenalty;
}

export function formatCuivres(n: number) {
  const zeroCuivreCoin = `<svg width="1em" height="1em" viewBox="0 0 24 24" style="vertical-align:-0.15em;display:inline-block"><defs><radialGradient id="cuivreGrad" cx="50%" cy="40%" r="60%"><stop offset="0%" stop-color="#F5D0C5"/><stop offset="45%" stop-color="#B87333"/><stop offset="100%" stop-color="#7A3E1A"/></radialGradient></defs><circle cx="12" cy="12" r="11" fill="url(#cuivreGrad)" stroke="#5A2E12" stroke-width="1.5"/><text x="12" y="16.5" font-size="12" font-weight="800" text-anchor="middle" fill="#3E1E0A" style="font-family:'Cinzel',serif">C</text></svg>`;
  if (!n || n <= 0) return `0 <span class="currency-cuivre-zero">${zeroCuivreCoin}</span>`;

  const platine = Math.floor(n / 1000000000);
  const or = Math.floor((n % 1000000000) / 1000000);
  const argent = Math.floor((n % 1000000) / 1000);
  const cuivre = Math.floor(n % 1000);

  const parts = [];
  const platineCoin = `<svg width="1em" height="1em" viewBox="0 0 24 24" style="vertical-align:-0.15em;display:inline-block"><defs><radialGradient id="platineGrad" cx="50%" cy="40%" r="60%"><stop offset="0%" stop-color="#E8F6FA"/><stop offset="45%" stop-color="#AECBE0"/><stop offset="100%" stop-color="#6A9BB0"/></radialGradient></defs><circle cx="12" cy="12" r="11" fill="url(#platineGrad)" stroke="#5A7D8C" stroke-width="1.5"/><text x="12" y="16.5" font-size="12" font-weight="800" text-anchor="middle" fill="#365A6C" style="font-family:'Cinzel',serif">P</text></svg>`;
  const orCoin = `<svg width="1em" height="1em" viewBox="0 0 24 24" style="vertical-align:-0.15em;display:inline-block"><defs><radialGradient id="orGrad" cx="50%" cy="40%" r="60%"><stop offset="0%" stop-color="#FFF5C3"/><stop offset="45%" stop-color="#F5C518"/><stop offset="100%" stop-color="#B8860B"/></radialGradient></defs><circle cx="12" cy="12" r="11" fill="url(#orGrad)" stroke="#8B6508" stroke-width="1.5"/><text x="12" y="16.5" font-size="12" font-weight="800" text-anchor="middle" fill="#5A4000" style="font-family:'Cinzel',serif">O</text></svg>`;
  const argentCoin = `<svg width="1em" height="1em" viewBox="0 0 24 24" style="vertical-align:-0.15em;display:inline-block"><defs><radialGradient id="argentGrad" cx="50%" cy="40%" r="60%"><stop offset="0%" stop-color="#F7F9FA"/><stop offset="45%" stop-color="#C0C7CF"/><stop offset="100%" stop-color="#7B8794"/></radialGradient></defs><circle cx="12" cy="12" r="11" fill="url(#argentGrad)" stroke="#55606B" stroke-width="1.5"/><text x="12" y="16.5" font-size="12" font-weight="800" text-anchor="middle" fill="#3A424A" style="font-family:'Cinzel',serif">A</text></svg>`;
  const cuivreCoin = `<svg width="1em" height="1em" viewBox="0 0 24 24" style="vertical-align:-0.15em;display:inline-block"><defs><radialGradient id="cuivreGrad" cx="50%" cy="40%" r="60%"><stop offset="0%" stop-color="#F5D0C5"/><stop offset="45%" stop-color="#B87333"/><stop offset="100%" stop-color="#7A3E1A"/></radialGradient></defs><circle cx="12" cy="12" r="11" fill="url(#cuivreGrad)" stroke="#5A2E12" stroke-width="1.5"/><text x="12" y="16.5" font-size="12" font-weight="800" text-anchor="middle" fill="#3E1E0A" style="font-family:'Cinzel',serif">C</text></svg>`;
  if (platine > 0) parts.push(`<span class="currency-platine" title="Platine">${platine.toLocaleString('fr-FR')} ${platineCoin}</span>`);
  if (or > 0) parts.push(`<span class="currency-or" title="Or">${or.toLocaleString('fr-FR')} ${orCoin}</span>`);
  if (argent > 0) parts.push(`<span class="currency-argent" title="Argent">${argent} ${argentCoin}</span>`);
  if (cuivre > 0 || parts.length === 0) parts.push(`<span class="currency-cuivre" title="Cuivre">${cuivre} ${cuivreCoin}</span>`);

  return parts.join(' ');
}

export function cuivresToEuro(n: number) { return (n / 1000).toFixed(2); }

export function formatDate(iso: string | number | Date) {
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export function getFinalStats(): Record<string, number> {
  return computePlayerFinalStats(state, {
    classes: _contentRegistries?.classes || {},
    items: _contentRegistries?.items || {},
    titles: REGISTRIES.titles,
    elements: REGISTRIES.elements,
  }, { familiar: getFamiliarBonusStats(), receptionist: getReceptionistBonusStats() });
}

// Respec GRATUIT (décision 2026-07-27) : charger un build sauvegardé est
// gratuit — un respec payant à côté n'était que du théâtre. Rembourse TOUT :
// attributs (pool unique) + affinités/résistances élémentaires.
export function resetStats() {
  const level = state.player.level || 1;
  state.player.statPoints = 0;
  state.player.secondaryStatPoints = secondaryStatPointsBudget(level);
  state.player.tertiaryStatPoints = tertiaryStatPointsBudget(level);
  state.player.stats_added = { ...DEFAULT_STATE.player.stats_added };
  state.player.secondaryStatsAdded = { ...DEFAULT_STATE.player.secondaryStatsAdded };
  state.player.tertiaryStatsAdded = { ...DEFAULT_STATE.player.tertiaryStatsAdded };
  return true;
}

export function addTertiaryStat(key: string) {
  if (!state.player) return;
  if ((state.player.tertiaryStatPoints || 0) <= 0) return;
  const allowed = ['affinityGlobal', 'resistanceGlobal', ...Object.keys(REGISTRIES.elements).filter(id => id !== 'physical').flatMap(id => [`affinity_${id}`, `resistance_${id}`])];
  if (!allowed.includes(key)) return;
  state.player.tertiaryStatPoints--;
  if (!state.player.tertiaryStatsAdded) state.player.tertiaryStatsAdded = { ...DEFAULT_STATE.player.tertiaryStatsAdded };
  if (state.player.tertiaryStatsAdded[key] === undefined) state.player.tertiaryStatsAdded[key] = 0;
  state.player.tertiaryStatsAdded[key]++;
}

export function getEffectiveElementAffinity(elementId: string) {
  const final = getFinalStats();
  return final[`affinity_${elementId}`] || final.affinityGlobal || 0;
}

export function getEffectiveElementResistance(elementId: string) {
  const final = getFinalStats();
  return final[`resistance_${elementId}`] || final.resistanceGlobal || 0;
}

// Gratuit, même logique que resetStats (les builds restaurent les sorts).
export function resetSkills() {
  const level = state.player.level || 1;
  state.player.skill_points = skillPointsBudget(level);
  state.player.skills_unlocked = [];
  return true;
}

export function grantEquipmentInstance(instance: EquipmentInstance) {
  if (!instance?.instanceId) return false;
  checkCodexUnlock(`obtain:${instance.templateId}`);
  if (!Array.isArray(state.equipmentInventory)) state.equipmentInventory = [];
  if (state.equipmentInventory.some((entry: EquipmentInstance) => entry.instanceId === instance.instanceId)) return false;
  state.equipmentInventory.push(instance);
  return true;
}

export function spendCuivres(amount: number, desc?: string) {
  if (!state.wallet) return false;
  if ((state.wallet.cuivres || 0) < amount) return false;
  state.wallet.cuivres -= amount;
  // Montant NÉGATIF, comme toutes les autres dépenses. Cette écriture
  // enregistrait autrefois le montant POSITIF :
  // une dépense apparaissait donc comme un crédit dans l'historique. Le solde,
  // lui, était bien décrémenté juste au-dessus, et l'affichage masquait
  // l'anomalie en prenant la valeur absolue — mais toute somme faite un jour sur
  // cet historique (export, statistique, synchronisation) aurait été fausse.
  state.wallet.transactions.unshift({ id: Date.now(), date: new Date().toISOString(), desc, amount: -amount, type: 'spend' });
  if (state.wallet.transactions.length > 50) state.wallet.transactions.pop();
  return true;
}

export const showLyraModal = ref(false);
export const showNexusModal = ref(false);
export const activeNexusPopup = ref<string | null>(null);
// Donjons v2 (MAJ-08 B1) : éditeur et Hall of Fame en OVERLAYS (pas des routes)
// → s'ouvrent par-dessus le monde courant (carte ou donjon) sans le décharger.
export const showDungeonEditor = ref(false);
export const showHallOfFame = ref(false);
// La route de run démonte la carte. Ce drapeau coupe les autres surfaces WebGL
// globales pendant l'exploration afin de réserver le GPU au donjon.
export const dungeonRunImmersive = ref(false);
// Onglet de création à ouvrir dans le hub /donjon quand on y arrive depuis un
// nœud Nexus (quête/événement/donjon). Consommé (remis à null) au montage du hub.
export const nexusDonjonMode = ref<'quest' | 'event' | 'dungeon' | 'sponsor-list' | null>(null);
// Onglet à ouvrir dans la Taverne quand on l'ouvre depuis un nœud dédié du Nexus
// (ex. « Clan »). Consommé (remis à null) au montage de la Taverne.
export const taverneInitialTab = ref<'faits' | 'bestiaire' | 'souvenirs' | 'discuter' | 'amis' | 'clan' | 'hall' | null>(null);
export const isCombatActive = ref(false);

// Une carte/fiche ou un panneau plein écran est ouvert (carte de monstre/joueur,
// meute, quête, Rift, duel…). Le HUD Astrolabe flottant se masque alors : il vit
// dans un calque racine (z-index 8000), alors que ces panneaux vivent DANS
// #screen (stacking-context à z-index 1). Un panneau piégé dans #screen ne peut
// donc jamais repasser au-dessus du HUD par z-index seul — on masque le HUD.
export const cardOverlayOpen = ref(false);
